import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ExpenseIcon from "@mui/icons-material/ReceiptLong";
import SettlementIcon from "@mui/icons-material/Handshake";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import DI from "./utility/DI";

const Notifications = (props) => {
  const navigate = useNavigate();
  const {
    di: { session, GET, urls, error },
  } = props;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await GET(urls.get.get_notifications, { user_id: session?.id });
      if (res?.success) {
        setNotifications(res.data || []);
      }
    } catch (e) {
      error("Failed to load notifications");
    }
    setLoading(false);

    // Mark as read after loading
    try {
      GET(urls.get.mark_notifications_read, { user_id: session?.id });
    } catch (e) {}
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case "expense_added":
        return <ExpenseIcon sx={{ color: "primary.main" }} />;
      case "expense_acknowledged":
        return <CheckCircleIcon sx={{ color: "success.main" }} />;
      case "settlement_requested":
      case "settlement_confirmed":
        return <SettlementIcon sx={{ color: "info.main" }} />;
      default:
        return <NotificationsIcon sx={{ color: "text.secondary" }} />;
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString.replace(" ", "T"));
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", pb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: "text.primary" }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Notifications</Typography>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <NotificationsIcon sx={{ fontSize: 60, color: "action.disabled", mb: 2 }} />
            <Typography color="text.secondary">No notifications yet.</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((n, idx) => (
              <React.Fragment key={n.id}>
                {idx > 0 && <Divider />}
                <ListItem 
                  alignItems="flex-start"
                  sx={{ 
                    bgcolor: n.is_read ? "transparent" : "action.hover",
                    "&:hover": { bgcolor: "action.selected" },
                    cursor: "pointer",
                    py: 2
                  }}
                  onClick={() => {
                    // Navigate based on type
                    if (n.type.includes("expense")) navigate("/panel/transactions");
                    else if (n.type.includes("settlement")) navigate("/panel/transactions");
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
                      {getIcon(n.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight={n.is_read ? 400 : 600}>
                        {n.message}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(n.created_at)}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
      
      {!loading && notifications.length > 0 && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button size="small" color="inherit" onClick={fetchNotifications}>Refresh</Button>
        </Box>
      )}
    </Box>
  );
};

export default DI(Notifications);
